"use client";

import { useEffect, useState } from "react";
import { logger } from "@/lib/log";
import { installErrorReporter } from "@/lib/error-reporter";
import {
  isChunkLoadError,
  shouldAttemptChunkReload,
  triggerChunkReload,
} from "@/lib/chunk-load-error";

const log = logger("app/error");

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reloading] = useState(
    () => isChunkLoadError(error) && shouldAttemptChunkReload(),
  );

  useEffect(() => {
    // On a first-load route crash this child effect runs before the parent
    // Providers effect installs the reporter, so install here too before
    // logging or the report is missed. Idempotent.
    installErrorReporter();
    if (reloading) {
      log.warn("chunk load error in route boundary; auto-reloading", {
        message: error.message,
      });
      triggerChunkReload();
      return;
    }
    log.error("route error boundary caught", {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
    });
  }, [error, reloading]);

  if (reloading) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full text-center space-y-2">
          <div className="text-sm text-subtle">Updating to the latest version…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-3xl">⚠️</div>
        <h2 className="text-lg font-semibold">Something went wrong</h2>
        <p className="text-sm text-subtle break-words">
          {error.message || "An unexpected error occurred while rendering this view."}
        </p>
        {error.digest && (
          <p className="text-[10px] text-subtle/70 font-mono">ref: {error.digest}</p>
        )}
        <div className="flex justify-center gap-2 pt-2">
          <button
            onClick={() => reset()}
            className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
